import React from 'react';
import { FIELD } from '../../components/ui/tokens';
import { paymentRunMessage } from '../../lib/paymentRunsApi';
import { formatReportDay, formatReportMoney, supplierLabel } from '../accountsPayableReports/format';
import { ROW_PROBLEM_TEXT, rowProblem, sectionTotal, tickedCount } from './runEditing';
import type { EditRow, EditSection } from './runEditing';

/**
 * One supplier (in one currency) in the run editor: its bills with a tick and an amount to pay
 * against each, the supplier's total, and the reference its payment will carry. A bill on hold or
 * disputed is listed but never ticked for the person; a bill can be part-paid, never more than it
 * owes. Every word about a bill's state is decided here from data, never from server prose.
 */
export interface RunSectionProps {
  section: EditSection;
  /** Nothing can change (the run is being saved or recorded). */
  disabled: boolean;
  onToggle: (billId: string) => void;
  onAmount: (billId: string, text: string) => void;
  onReference: (text: string) => void;
}

const CELL = 'px-3 py-2 align-top';

/** The words beside a bill that say why it deserves a second look. Nothing for an ordinary bill. */
function notesFor(row: EditRow): string[] {
  const notes: string[] = [];
  if (row.status === 'on_hold') notes.push('On hold');
  else if (row.status === 'disputed') notes.push('Disputed');
  else if (row.status === 'overdue') notes.push('Overdue');
  else if (row.status === 'partially_paid') notes.push('Part paid');
  if (row.changed) notes.push('Owes less than saved');
  if (row.inRuns.length) notes.push(`Also in ${row.inRuns.join(', ')}`);
  return notes;
}

export const RunSection: React.FC<RunSectionProps> = ({ section, disabled, onToggle, onAmount, onReference }) => {
  const name = supplierLabel(section.supplierName);
  const ticked = tickedCount(section);
  const failed = section.groupStatus === 'failed';

  return (
    <section
      aria-label={`${name}, ${section.currency}`}
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="run-section"
    >
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-slate-200 px-3 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-900">{name}</h3>
          <p className="text-xs text-slate-500">
            {section.currency}, {ticked} of {section.rows.length} {section.rows.length === 1 ? 'bill' : 'bills'} ticked
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
          <div>
            <div className="mb-1 text-xs font-medium text-slate-600">To pay</div>
            <div className="text-sm font-semibold tabular-nums text-slate-900" data-testid="section-total">
              {formatReportMoney(sectionTotal(section), section.currency)}
            </div>
          </div>
          <div>
            {section.savedReference !== null ? (
              <>
                <label htmlFor={`ref-${section.key}`} className="mb-1 block text-xs font-medium text-slate-600">
                  Payment reference
                </label>
                <input
                  id={`ref-${section.key}`}
                  value={section.reference}
                  disabled={disabled}
                  onChange={(e) => onReference(e.target.value)}
                  className={`${FIELD} h-9 w-52 border-slate-300 bg-white`}
                  aria-label={`Payment reference for ${name}`}
                />
              </>
            ) : (
              <p className="max-w-[14rem] text-xs text-slate-500">The reference is given when you save the run.</p>
            )}
          </div>
        </div>
      </header>

      {failed ? (
        <p role="alert" className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          Not recorded: {paymentRunMessage(section.errorCode)} Nothing was paid to this supplier.
        </p>
      ) : null}

      <div className="relative overflow-x-auto">
        <table className="data-table w-full min-w-[40rem] text-left text-sm">
          <caption className="sr-only">Bills for {name} in {section.currency}</caption>
          <thead className="bg-slate-50">
            <tr className="text-xs text-slate-600">
              <th scope="col" className={`${CELL} w-10`}>
                <span className="sr-only">Pay</span>
              </th>
              <th scope="col" className={CELL}>Invoice</th>
              <th scope="col" className={CELL}>Due</th>
              <th scope="col" className={`${CELL} text-right`}>Owes</th>
              <th scope="col" className={`${CELL} text-right`}>Pay now</th>
              <th scope="col" className={CELL}>Note</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row) => {
              const problem = rowProblem(row);
              const notes = notesFor(row);
              return (
                <tr key={row.billId} className={row.ticked ? '' : 'text-slate-500'}>
                  <td className={CELL}>
                    <input
                      type="checkbox"
                      checked={row.ticked}
                      disabled={disabled}
                      onChange={() => onToggle(row.billId)}
                      aria-label={`Pay invoice ${row.invoiceNumber}`}
                    />
                  </td>
                  <td className={`${CELL} font-medium`}>{row.invoiceNumber || '--'}</td>
                  <td className={`${CELL} whitespace-nowrap`}>{formatReportDay(row.dueDate)}</td>
                  <td className={`${CELL} text-right tabular-nums whitespace-nowrap`}>
                    {row.outstanding === null ? '--' : formatReportMoney(row.outstanding, section.currency)}
                  </td>
                  <td className={`${CELL} text-right`}>
                    <input
                      inputMode="decimal"
                      value={row.amount}
                      disabled={disabled || !row.ticked}
                      onChange={(e) => onAmount(row.billId, e.target.value)}
                      aria-label={`Amount to pay against ${row.invoiceNumber}`}
                      aria-invalid={problem ? true : undefined}
                      className={`${FIELD} h-8 w-28 text-right tabular-nums ${problem ? 'border-red-400' : 'border-slate-300'} bg-white`}
                    />
                    {problem ? (
                      <div role="alert" className="mt-1 text-xs text-red-700">
                        {ROW_PROBLEM_TEXT[problem]}
                      </div>
                    ) : null}
                  </td>
                  <td className={`${CELL} text-xs text-slate-600`}>{notes.join('; ')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default RunSection;
